import { ControllerInterface } from "csi/action/Controller";
import JsonResponse from "csi/comm/JsonResponse";
import { getSlimCardAttrsAsString } from "csi/middleware/card/SlimCardStore";
import { ControllerSaveFunction } from "csi/middleware/common";
import WorkingCache from "csi/state/WorkingCache";
import { C8Node, cnl, ICache } from "csi/typings";
import { ReactionStore } from "csi/utility/mobxUtility";
import { CheckboxTreeNode } from "designSystem/components/checkbox/CheckboxTree";
import { RadioOption } from "designSystem/components/radio/RadioGroup";
import { ILocaleProvider } from "designSystem/locale";
import { action, computed, makeObservable, observable } from "mobx";
import { createContext, useContext } from "react";
import { actionResource, pushUpdateFromStyle } from "./PushFromStyleService";


export class PushFromStyleStore extends ReactionStore {
	private readonly actionController: ControllerInterface;

	private readonly cache: ICache = new WorkingCache();

	protected readonly locale: ILocaleProvider;

	private readonly onSave: ControllerSaveFunction;

	private readonly styleSizeChartRevURL: cnl;

	readonly titleRes: string = "PushUpdateToShape";

	readonly addDSLines: CheckboxTreeNode;

	@observable.ref filteredAddDSLines: CheckboxTreeNode;

	readonly updateDSLines: CheckboxTreeNode;

	@observable.ref filteredUpdateDSLines: CheckboxTreeNode;

	@observable restoreAllDSLines: boolean;

	@observable searchText: string;

	constructor(
		ac: ControllerInterface,
		locale: ILocaleProvider,
		onSave: ControllerSaveFunction,
		addDSLines: CheckboxTreeNode,
		updateDSLines: CheckboxTreeNode,
	) {
		super();
		makeObservable(this);
		ac.bShowWait = true;

		this.actionController = ac;
		this.cache = ac.getCache();
		this.locale = locale;
		this.styleSizeChartRevURL = ac.m_urlParent;

		this.addDSLines = addDSLines;
		this.filteredAddDSLines = addDSLines;
		this.updateDSLines = updateDSLines;
		this.filteredUpdateDSLines = updateDSLines;

		this.restoreAllDSLines = true;
		this.onSave = onSave;
	}

	/** Returns true if update dimensions section should be displayed */
	get shouldDisplayUpdateSection(): boolean {
		const { filteredUpdateDSLines } = this;
		return filteredUpdateDSLines.hasChildren;
	}

	/** Returns true if add dimensions section should be displayed */
	get shouldDisplayAddSection(): boolean {
		const { filteredAddDSLines, restoreAllDSLines } = this;
		return restoreAllDSLines && filteredAddDSLines.hasChildren;
	}

	get revisionNode(): C8Node {
		const { cache, styleSizeChartRevURL } = this;
		return cache.get(styleSizeChartRevURL);
	}

	get masterNode(): C8Node {
		const { cache, revisionNode } = this;
		return cache.get(revisionNode.__Parent__);
	}

	/** Get the number of DSLines selected */
	@computed get dsLinesSelected(): number {
		const { filteredAddDSLines, restoreAllDSLines, filteredUpdateDSLines } = this;
		const count = filteredUpdateDSLines.selectedNodes.length;
		return restoreAllDSLines ? count + filteredAddDSLines.selectedNodes.length : count;
	}

	/** If the subtype is not all locked, add an option to only update existing Style SC DSLines */
	get pushSettings(): RadioOption<boolean>[] {
		const { locale } = this;
		const options = [{
			name: locale.resource(actionResource("UpdateAllDSLines")),
			helperTextRes: actionResource("UpdateAllDSLinesHelperText"),
			value: true,
		}];
		options.push({
			name: locale.resource(actionResource("OnlyUpdateDSLines")),
			helperTextRes: actionResource("OnlyUpdateDSLinesHelperText"),
			value: false,
		});
		return options;
	}

	/**
	 * On save handler
	 */
	public save = async (): Promise<boolean> => {
		const { onSave, onSavePromise } = this;
		onSave(onSavePromise());
		return true;
	};

	/**
	 * Promise to send the request to Restore From Shape
	 */
	public onSavePromise = async (): Promise<JsonResponse> => {
		const { actionController, filteredAddDSLines, restoreAllDSLines, styleSizeChartRevURL, filteredUpdateDSLines } = this;
		const { m_writer } = actionController;
		try {
			const res = await pushUpdateFromStyle(styleSizeChartRevURL, restoreAllDSLines, filteredUpdateDSLines, filteredAddDSLines, m_writer);
			return res;
		} catch (err) {
			throw err;
		}
	};

	/** page action message */
	get actionMessage(): string {
		const { dsLinesSelected, locale } = this;
		return locale.resource(actionResource("DSLinesSelected"), [dsLinesSelected]);
	}

	/* Filter Dimensions */
	getFilteredDimensions(searchText: string, dimensions: CheckboxTreeNode): CheckboxTreeNode {
		if (searchText && dimensions.hasChildren) {
			const newFilteredDimensions = CheckboxTreeNode.createCheckboxTreeNode(dimensions, false);
			const filteredNodes = dimensions.children.filter((v) => {
				return getSlimCardAttrsAsString(v.uniqueId, this.cache)
					.toLocaleLowerCase()
					.includes(searchText);
			});
			filteredNodes.forEach((e) => {
				newFilteredDimensions.addChild(e);
			});
			return newFilteredDimensions;
		}
		return dimensions;
	}

	/** If option chosen is 'restore all', select all delete DSLines, otherwise deselect */
	@action onChangePushSettings = (event, value: boolean): void => {
		this.restoreAllDSLines = value;
	};

	/* Make search text lower case for use in filtering Dimensions */
	@action onChangeSearchTextField = (event): void => {
		this.searchText = event.target.value;
		const trimmedText = this.searchText.trim()
			.toLocaleLowerCase();
		this.filteredAddDSLines = this.getFilteredDimensions(trimmedText, this.addDSLines);
		this.filteredUpdateDSLines = this.getFilteredDimensions(trimmedText, this.updateDSLines);
	};
}

export const PushFromStyleStoreeContext = createContext<PushFromStyleStore>(undefined);
export const usePushFromStyleStore = (): PushFromStyleStore => useContext(PushFromStyleStoreeContext);